import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserDocumentsRoutes } from 'src/app/core/enums/routes.enum';
import { DepartmentApiService } from 'src/app/department/services/department-api.service';
import { IdName } from 'src/app/shared/models/id-name';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  getExternalDocumentIdFromPath,
  getLinkToDocumentByPublicationState,
} from '../functions/get-link-to-document';
import { IDocument } from '../models/document.model';
import { DocumentApiService } from './document-api.service';

interface DocumentDepartmentContext {
  departmentSpecific: boolean;
  departments: (IdName | string)[];
  menu?: { id: string };
}

export interface DocumentRedirectResult {
  path: string | null | undefined;
  departmentId: string | null;
}

const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class DocumentDepartmentResolverService {
  constructor(
    private documentApiService: DocumentApiService,
    private departmentApiService: DepartmentApiService,
    private store: Store
  ) {}

  resolveDepartment(
    document: DocumentDepartmentContext,
    preferredDepartmentId?: string
  ): Observable<string | null> {
    const preferredId =
      preferredDepartmentId === ALL_DEPS_TOKEN ? undefined : preferredDepartmentId;

    if (document.departmentSpecific) {
      return of(this.pickMatchingDepartment(document.departments, preferredId));
    }

    if (!document.menu?.id) {
      return of(null);
    }

    return this.departmentApiService
      .getAllDepartmentsForMenuId(document.menu.id, true)
      .pipe(map((departments) => this.pickMatchingDepartment(departments, preferredId)));
  }

  resolveExternalRedirect(
    path: string | null | undefined,
    departmentId: string
  ): Observable<DocumentRedirectResult> {
    const documentId = getExternalDocumentIdFromPath(path);
    if (!documentId) {
      return of({ path, departmentId: this.resolveProvidedDepartmentId(departmentId) });
    }

    return this.documentApiService.loadDocumentById(documentId, true).pipe(
      switchMap((document) =>
        this.resolveDepartmentId(document, departmentId).pipe(
          map((resolvedDepartmentId) =>
            this.buildDocumentRedirectResult(document, departmentId, resolvedDepartmentId)
          )
        )
      ),
      catchError((error) => {
        console.error('Error resolving external document link:', error);
        return of({ path: UserDocumentsRoutes.Main, departmentId: null });
      })
    );
  }

  private resolveProvidedDepartmentId(departmentId: string): string | null {
    return this.isZeroDepartmentId(departmentId) ? null : departmentId;
  }

  private resolveDepartmentId(
    document: IDocument,
    departmentId: string
  ): Observable<string | null> {
    if (!this.isZeroDepartmentId(departmentId)) {
      return of(departmentId);
    }

    return this.resolveDepartment(document);
  }

  private buildDocumentRedirectResult(
    document: IDocument,
    departmentId: string,
    resolvedDepartmentId: string | null
  ): DocumentRedirectResult {
    if (this.isZeroDepartmentId(departmentId) && !resolvedDepartmentId) {
      return { path: UserDocumentsRoutes.Main, departmentId: null };
    }

    const { link, referrerType } = getLinkToDocumentByPublicationState(document);
    return {
      path: `${link}?referrerType=${encodeURIComponent(referrerType)}`,
      departmentId: resolvedDepartmentId,
    };
  }

  private isZeroDepartmentId(id: string): boolean {
    return id === ZERO_UUID;
  }

  private pickMatchingDepartment(
    departments: (IdName | string)[] | undefined,
    preferredDepartmentId?: string
  ): string | null {
    const departmentIds = (departments ?? [])
      .map((department) => (typeof department === 'string' ? department : department?.id))
      .filter((id): id is string => typeof id === 'string');
    if (preferredDepartmentId && departmentIds.includes(preferredDepartmentId)) {
      return preferredDepartmentId;
    }

    const userDepartments = this.store.selectSnapshot(
      TenantUserAccessState.getAllAvailableDepartments
    );
    return userDepartments.find(({ id }) => departmentIds.includes(id))?.id ?? null;
  }
}
